import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import { useAuth } from "@/hooks/useAuth";
import { TransferRequestMainDataCard } from "@/components/common/section-components/card/TransferRequestMainDataCard";
import { TransferRequestDetailLegend } from "@/components/common/section-components/requests/transfer/TransferRequestDetailLegend";
import { TransferRequestMessageList } from "@/components/common/section-components/requests/transfer/TransferRequestMessageList";

import { ScheduledTransferDetail } from "../schema/scheduled-transfer.schema";
import { getScheduledTransferRequest } from "@/services/scheduled-transfer-request.service";
import { ScheduledTransferTransactionType } from "./ScheduledTransferTransactionType";
import { ScheduledTransferStatus } from "@/constants/entity-status";
import {
  handleExecuteRequestHandler,
  handleRejectRequestHandler,
} from "../schema/handler-functions";
import { TransferRequestAmountAndActions } from "@/components/common/section-components/requests/transfer/TransferRequestAmountAndActions";
import { useRoles } from "@/hooks/useRoles";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { toaster } from "@/components/ui/toaster";

export const ScheduledTransferRequestDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();

  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.SCHEDULED_TRANSFER_REQUESTS);
  };

  const [loadingRequest, setLoadingRequest] = useState<boolean>(false);

  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();

  const [request, setRequest] = useState<ScheduledTransferDetail | null>();

  const fetchRequest = async (showLoad: boolean = true) => {
    try {
    if (showLoad) setLoadingRequest(true);
    const response = await getScheduledTransferRequest({
      id: Number(id),
    });

    setRequest(response);

    setLoadingRequest(false);
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.message ?? "No se pudo cargar la transferencia programada.",
        });
      }
      throw error;
    }
  };

  useEffect(() => {
    if (isActive && id && user) fetchRequest();
  }, [isActive, id, user]);

  const {
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(
      z.object({
        request_amount: z.any().optional(),
      }),
    ),
    defaultValues: {
      request_amount: request?.montoSolicitado,
    },
  });

  const watchedValues = useWatch({
    control,
  });

  useEffect(() => {
    if (request) {
      setValue("request_amount", request?.montoSolicitado);
    }
  }, [request]);

  useEffect(() => {
    clearErrors("request_amount");

    const monto = formatCurrencyInputToNumber(watchedValues?.request_amount);

    if (monto < 1) {
      setError("request_amount", {
        message: "El valor debe ser mayor o igual a $1.",
      });
    } else {
      clearErrors("request_amount");
    }
  }, [watchedValues?.request_amount, errors]);

  const [loadingRejectRequest, setLoadingRejectRequest] =
    useState<boolean>(false);

  const [loadingExecuteRequest, setLoadingExecuteRequest] =
    useState<boolean>(false);

    const { fetchPermissions } = useRoles();

  const handleExecuteRequest = async () => {
    await handleExecuteRequestHandler({
      navigate,
      setLoadingExecuteRequest,
      request,
      fetchPermissions
    });
  };

  const handleRejectRequest = async (event?: FieldValues) => {
    await handleRejectRequestHandler({
      navigate,
      setLoadingRejectRequest,
      setOpenRejectModal,
      event,
      request,
    fetchPermissions
    });
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={`Transferencia programada N°: ${request?.id || ""}`}
        onBack={handleBack}
        backButton
        loading={loadingRequest}
      />

      {!loadingRequest && (
        <ScheduledTransferTransactionType
          request={request}
          loadingRejectRequest={loadingRejectRequest}
          loadingExecuteRequest={loadingExecuteRequest}
          loadingRequest={loadingRequest}
          setOpenRejectModal={setOpenRejectModal}
          handleExecuteRequest={handleExecuteRequest}
        />
      )}

      {!loadingRequest && (
        <>
          <TransferRequestAmountAndActions request={request} scheduled />

          <TransferRequestMainDataCard request={request} />

          <Flex
            flexDir="column"
            padding="12px"
            height="100%"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            borderRadius="12px"
            gap="25px"
          >
            <TransferRequestDetailLegend />

            <TransferRequestMessageList request={request} />
          </Flex>
        </>
      )}

      {request?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectRequest}
            loadingAccept={loadingRejectRequest}
            idTipoSolicitud={5}
            isCancelling={request?.estado == ScheduledTransferStatus.SCHEDULED}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);
